import { useRef, useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useNotifications, notifStore, type Notification } from '../stores/notifications.js';

const SEV_COLOR: Record<string, string> = {
  info:    'var(--accent-blue)',
  warn:    'var(--accent-yellow)',
  error:   'var(--accent-red)',
  success: 'var(--accent-green)',
};

const SEV_ICON: Record<string, string> = {
  info:    '◎',
  warn:    '⚠',
  error:   '✕',
  success: '✓',
};

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function NotifRow({ n }: { n: Notification }) {
  const navigate = useNavigate();
  const color = SEV_COLOR[n.severity] ?? 'var(--accent-blue)';

  function handleClick() {
    notifStore.markRead(n.id);
    if (n.href) void navigate({ to: n.href as '/' });
  }

  return (
    <div
      className="group flex gap-3 px-4 py-3 transition-colors duration-100 cursor-pointer"
      style={{
        background: n.read ? 'transparent' : 'rgba(13,207,184,0.06)',
        borderBottom: '1px solid var(--border)',
      }}
      onClick={handleClick}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(13,207,184,0.10)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = n.read ? 'transparent' : 'rgba(13,207,184,0.06)')}
    >
      <span
        className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
        style={{ background: `${color}22`, color, border: `1px solid ${color}44` }}
      >
        {SEV_ICON[n.severity] ?? '◎'}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold leading-snug" style={{ color: n.read ? 'var(--text-secondary)' : 'var(--text-primary)' }}>
          {n.title}
        </div>
        {n.body && (
          <div className="text-[11px] leading-snug mt-0.5 line-clamp-2" style={{ color: 'var(--text-dimmed)' }}>
            {n.body}
          </div>
        )}
        <div className="mt-1 text-[10px]" style={{ color: 'var(--text-dimmed)' }}>{timeAgo(n.ts)}</div>
      </div>
      {!n.read && (
        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: 'var(--accent-blue)' }} />
      )}
      <button
        type="button"
        className="ml-1 shrink-0 opacity-0 group-hover:opacity-100 text-[11px] transition-opacity"
        style={{ color: 'var(--text-dimmed)' }}
        onClick={(e) => { e.stopPropagation(); notifStore.dismiss(n.id); }}
        title="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const notifs = useNotifications();
  const unread = notifs.filter((n) => !n.read).length;

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
        style={{
          background: open ? 'rgba(13,207,184,0.15)' : 'var(--bg-tertiary)',
          border: '1px solid var(--border)',
          color: 'var(--text-secondary)',
        }}
        title="Notifications"
      >
        <span className="text-sm">◉</span>
        {unread > 0 && (
          <span
            className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full px-0.5 text-[9px] font-bold text-white"
            style={{ background: 'var(--accent-red)' }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute bottom-10 left-0 z-50 flex w-80 flex-col rounded-2xl overflow-hidden shadow-2xl"
          style={{
            background: 'rgba(10,42,50,0.97)',
            border: '1px solid var(--glass-border)',
            backdropFilter: 'blur(18px)',
            maxHeight: '420px',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              Notifications {unread > 0 && <span className="ml-1 text-xs font-normal" style={{ color: 'var(--accent-blue)' }}>({unread} new)</span>}
            </span>
            <div className="flex items-center gap-2">
              {notifs.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => notifStore.markAllRead()}
                    className="text-[11px] transition-colors"
                    style={{ color: 'var(--accent-blue)' }}
                  >
                    Mark all read
                  </button>
                  <span style={{ color: 'var(--border)' }}>·</span>
                  <button
                    type="button"
                    onClick={() => notifStore.clear()}
                    className="text-[11px] transition-colors"
                    style={{ color: 'var(--text-dimmed)' }}
                  >
                    Clear all
                  </button>
                </>
              )}
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {notifs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <span className="text-2xl opacity-30">◉</span>
                <span className="text-xs" style={{ color: 'var(--text-dimmed)' }}>No notifications yet</span>
              </div>
            ) : (
              notifs.map((n) => <NotifRow key={n.id} n={n} />)
            )}
          </div>
        </div>
      )}
    </div>
  );
}
